// Copied from apps/web/src/libs/ui/components/Badge. Keep the two in sync by hand
// until the DS components move into packages/ui.
// NOTE: the `tertiary-mono` variant needs `font-azeret`, which borrow does not
// load — that variant will fall back to the default family here.
import { cn, type PolymorphProps } from "logic/utils";
import type { ElementType, ReactNode } from "react";


import { BADGE_VARIANTS, type BadgeVariant } from "./variants";

interface BadgeExtraProps {
  /**
   * Color variant. Matches Figma `comp/badge` 1:1 — `accent-1` / `accent-2` keep
   * Figma's positional names (no semantic aliasing).
   * @default "primary"
   */
  variant?: BadgeVariant;
  /**
   * Container height — `sm` = 16px, `md` = 20px. Matches Figma's `size` variant.
   * @default "sm"
   */
  size?: "sm" | "md";
  /**
   * Leading status dot (Figma `size=status`). Inherits the variant label color + opacity.
   * The dot is 5px but sits centred in the same 12px icon frame the `icon` slot
   * uses (Figma `icon-16` / `type=dot_5px`), which is what sets its spacing.
   * Combine with `icon` only when you actually want both (rare).
   * @default false
   */
  dot?: boolean;
  /**
   * Leading icon node (12–16px recommended). Inherits the variant label color
   * via `currentColor`, so icons should use `stroke="currentColor"` /
   * `fill="currentColor"` rather than hardcoded colors.
   */
  icon?: ReactNode;
}

/**
 * Polymorphic props for `<Badge>`. Defaults to `<span>`; pass `as="div"`,
 * `as="button"`, etc. to change the rendered element while preserving all
 * native props for that element.
 */
export type BadgeProps<T extends ElementType> = PolymorphProps<T, BadgeExtraProps>;

const SIZE_CONTAINER: Record<NonNullable<BadgeExtraProps["size"]>, string> = {
  sm: "h-4", // 16px (--container/xxxxs)
  md: "h-5", // 20px (--container/xxxs)
};

const SIZE_INNER_PADDING: Record<NonNullable<BadgeExtraProps["size"]>, string> = {
  sm: "px-1.5 py-0.5", // 6px / 2px
  md: "px-1.5 py-1", // 6px / 4px
};

/**
 * `md` status-dot padding is asymmetric — 2px top, 6px right, 2px bottom, 4px
 * left — so the dot sits closer to the text than the container edge does.
 * Kept as its own literal lookup rather than derived from {@link SIZE_INNER_PADDING}:
 * Tailwind v4's JIT scanner only emits CSS for class strings it can see as
 * literals in source, so this can't be built via interpolation or `.replace()`.
 */
const SIZE_DOT_INNER_PADDING: Record<NonNullable<BadgeExtraProps["size"]>, string> = {
  sm: "px-1.5 py-0.5",
  md: "py-0.5 pr-1.5 pl-1",
};

/**
 * Text-bearing badge — small inline pill for labels, statuses, and short
 * metadata. Mirrors Figma `comp/badge` text variants (design-system nodes
 * `575:4203`, `1118:21412`, `810:32241`).
 *
 * For square icon-only badges use {@link IconBadge} instead — different shape
 * and size grammar, intentionally a separate component.
 *
 * @example Basic label
 * ```tsx
 * <Badge>New</Badge>
 * ```
 *
 * @example With leading status dot
 * ```tsx
 * <Badge dot variant="secondary">Term sheet signed</Badge>
 * ```
 *
 * @example With leading icon
 * ```tsx
 * <Badge icon={<ClockIcon className="size-3" />} variant="warning">Pending</Badge>
 * ```
 *
 * @example Mono variant for short identifiers
 * ```tsx
 * <Badge variant="tertiary-mono">0x58MX…D6JS</Badge>
 * ```
 *
 * @example Larger 20px size with destructive intent
 * ```tsx
 * <Badge size="md" dot variant="destructive">Defaulted</Badge>
 * ```
 *
 * @example Render as a button (polymorphic)
 * ```tsx
 * <Badge as="button" type="button" onClick={...} variant="accent-1">
 *   Filter
 * </Badge>
 * ```
 *
 * @see {@link IconBadge} for the icon-only square variant.
 * @see Figma — https://www.figma.com/design/hCI9wopUpt49KQ7bWrDGuB/USD.AI-%7C-Design-System?node-id=575-4203
 */
export function Badge<T extends ElementType = "span">(props: BadgeProps<T>) {
  const { as, variant = "primary", size = "sm", dot = false, icon, className, children, componentRef, ...rest } = props;

  const Component = as ?? "span";
  const tokens = BADGE_VARIANTS[variant];
  const isMono = variant === "tertiary-mono";

  return (
    <Component
      ref={componentRef}
      {...rest}
      className={cn("inline-flex items-center justify-center rounded-full", tokens.bg, SIZE_CONTAINER[size], className)}
    >
      <span
        className={cn(
          "flex h-full items-center justify-center gap-0.5",
          dot ? SIZE_DOT_INNER_PADDING[size] : SIZE_INNER_PADDING[size],
          tokens.stateLayer,
        )}
      >
        {dot ? (
          <span aria-hidden className="flex size-3 shrink-0 items-center justify-center">
            <span className={cn("size-[5px] rounded-full", tokens.labelBg, tokens.labelOpacity)} />
          </span>
        ) : null}
        {icon ? (
          <span className={cn("flex shrink-0 items-center justify-center", tokens.label, tokens.labelOpacity)}>
            {icon}
          </span>
        ) : null}
        <span
          className={cn(
            "leading-none whitespace-nowrap",
            tokens.label,
            tokens.labelOpacity,
            isMono
              ? "font-azeret text-[11px] tracking-[-0.22px]"
              : size === "md"
                ? "text-label-md-400"
                : "text-label-sm-400",
          )}
        >
          {children}
        </span>
      </span>
    </Component>
  );
}
